"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Bell, Users, MoreVertical } from "lucide-react";
import { toast } from "react-hot-toast";
import { IoIosNotificationsOutline } from "react-icons/io";
import { RxExit } from "react-icons/rx";
import { REST_API_URL } from "../../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function CommunityView({ community, onBack }) {
    const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [notifications, setNotifications] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [infoTab, setInfoTab] = useState("members");
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    
    const userTimeZone = getUserTimeZone();
    
    const headers = buildHeaders(token);
    
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    const handleLeaveCommunity = () => {
        setShowLeaveModal(true);
    };

    const confirmLeave = async () => {
        try {
            const res = await fetch(`${API_BASE}/community/community-leave/`, {
                method: "POST",
                headers,
                body: JSON.stringify({ community: community.id }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            setShowLeaveModal(false);
            onBack?.();
        } catch (err) {
            console.error("❌ Error leaving community:", err);
        }
    };

    const loadMembers = async () => {
        if (!community?.id) return;
        try {
            setMembersLoading(true);
            const res = await fetch(`${API_BASE}/community/community-members/${community.id}/`, {
                headers,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setMembers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Error loading members:", err);
            setMembers([]);
        } finally {
            setMembersLoading(false);
        }
    };

    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ 
                behavior: "smooth",
                block: "end"
            });
        }
    }, [messagesEndRef]);

    useEffect(() => {
        if (messages.length > 0) {
            setTimeout(scrollToBottom, 100);
        }
    }, [messages, scrollToBottom]);

    useEffect(() => {
        if (!loading && messages.length > 0) {
            setTimeout(scrollToBottom, 200);
        }
    }, [loading, messages, scrollToBottom]);

    useEffect(() => {
        if (infoTab === "members" && community?.id) {
            loadMembers();
        }
    }, [infoTab, community]);

    useEffect(() => {
        if (community?.id) {
            loadMessages();
            checkNotifications();
        }
    }, [community]);

    const loadMessages = async () => {
        if (!community?.id) return;

        try {
            setLoading(true);
            
            const res = await fetch(`${API_BASE}/community/community-messages/${community.id}/`, { headers });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            const messagesArray =
                data?.results?.messages ||
                data?.messages ||
                data?.results ||
                (Array.isArray(data) ? data : []);

            setMessages(messagesArray);
            
            setTimeout(scrollToBottom, 300);
        } catch (e) {
            console.error("❌ Error fetching messages:", e);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    const checkNotifications = async () => {
        try {
            const res = await fetch(`${API_BASE}/community/community-notification-check/${community.id}/`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setNotifications(data);
        } catch (e) {
            console.error("❌ Error checking notifications:", e);
        }
    };

    const checkNotificationsStatus = async () => {
        try {
            const res = await fetch(`${API_BASE}/community/community-notification-check/${community.id}/`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setNotificationsEnabled(!!data.notification);
        } catch (e) {
            console.error("❌ Error checking notification status:", e);
        }
    };

    const toggleNotifications = async () => {
        if (!community?.id || toggleLoading) return;

        setToggleLoading(true);

        try {
            const res = await fetch(`${API_BASE}/community/community-notification/${community.id}/`, {
                method: "GET",
                headers
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            const newState = Boolean(data.notification);
            setNotificationsEnabled(newState);

            toast.success(newState ? "🔔 Notifications ON" : "🔕 Notifications OFF", {
                position: "bottom-left",
            });

        } catch (err) {
            console.error("Notification toggle error:", err);
            toast.error("Failed to update notifications", { position: "bottom-left" });
        }

        setToggleLoading(false);
    };

    const handleSendMessage = async () => {
        if (!messageText.trim() || !community?.id) return;

        const tempId = Date.now();
        const newMessage = {
            id: tempId,
            text: messageText,
            self: true,
            created_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, newMessage]);
        setMessageText("");

        setTimeout(scrollToBottom, 50);

        try {
            setSending(true);
            
            const response = await fetch(
                `${API_BASE}/community/community-message-post/`,
                {
                    method: "POST",
                    headers,
                    body: JSON.stringify({
                        community: community.id,
                        text: newMessage.text,
                    }),
                }
            );

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || `HTTP ${response.status}`);
            }

            const result = await response.json();

            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === tempId
                        ? {
                            ...msg,
                            id: result.id || result.message_id || msg.id,
                            created_at: result.created_at || msg.created_at,
                            self: true,
                        }
                        : msg
                )
            );
            
        } catch (e) {
            console.error("❌ Error sending message:", e);
            toast.error("Failed to send message", { position: "bottom-center" });
            
            setMessages((prev) => prev.filter(msg => msg.id !== tempId));
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "";
        try {
            return new Date(timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                timeZone: userTimeZone,
            });
        } catch {
            return "";
        }
    };

    useEffect(() => {
        if (showInfo && infoTab === "members") {
            loadMembers();
        }

        if (showInfo && community?.id) {
            checkNotificationsStatus();
        }
    }, [showInfo, infoTab, community]);

    return (
        <div className="flex flex-col h-full bg-white rounded-xl overflow-hidden relative">
            <div className="flex items-center justify-between px-6 py-4 bg-[#F6F6F6] rounded-t-2xl border-b">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-white/60 rounded-full transition"
                    >
                        <ArrowLeft size={20} className="text-[#302B2B]" />
                    </button>

                    <img
                        src={community.icon || "/default-avatar.png"}
                        className="w-11 h-11 rounded-full object-cover"
                        alt="Community Icon"
                    />

                    <div className="flex flex-col leading-tight">
                        <span className="text-[18px] font-normal text-[#302B2B]">
                            {community.name}
                        </span>

                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 bg-[#07AC07] rounded-full"></span>
                            <span className="text-[14px] text-[#9A99AB]">
                                {(community.joined_count || 0).toLocaleString()} Online
                            </span>
                        </div>
                    </div>
                </div>

                <button
                    onClick={() => setShowInfo(true)}
                    className="p-2 hover:bg-white/60 rounded-full transition"
                >
                    <MoreVertical size={22} className="text-[#302B2B]" />
                </button>
            </div>

            <div 
                className="flex-1 overflow-y-auto p-4"
                ref={messagesContainerRef}
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-40">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5563F6] mb-3"></div>
                        <p className="text-gray-500 text-sm">Loading messages...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                        <div className="text-center max-w-sm">
                            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Send size={24} className="text-gray-400" />
                            </div>
                            <p className="text-lg font-medium text-gray-500 mb-2">No messages yet</p>
                            <p className="text-sm text-gray-400">Be the first to start the conversation!</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {messages.map((message, index) => {
                            const isSelf = message.self || message.is_own_message;

                            return (
                                <motion.div
                                    key={message.id || `msg-${index}`}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className={`w-full flex ${isSelf ? "justify-end" : "justify-start"}`}
                                >
                                    <div className={`flex items-start gap-3 max-w-[85%] ${isSelf ? "flex-row-reverse" : ""}`}>

                                        {!isSelf && (
                                            <img
                                                src={message.user_icon || "/default-avatar.png"}
                                                alt={message.full_name || "User"}
                                                className="w-9 h-9 rounded-full object-cover border border-gray-200"
                                            />
                                        )}

                                        <div className="flex flex-col max-w-[100%]">
                                            <div
                                                className={`
                                                    px-5 py-3 rounded-2xl
                                                    ${isSelf
                                                        ? "bg-[#5863ED] text-white rounded-bl-2xl"
                                                        : "bg-[#F6F6F6] text-[#302B2B] rounded-br-2xl"
                                                    }
                                                `}
                                            >
                                                {!isSelf && (
                                                    <p className="text-sm font-semibold text-[#5863ED] mb-1">
                                                        {message.full_name || message.user_email}
                                                    </p>
                                                )}

                                                <p className="text-base leading-relaxed break-words">
                                                    {message.text}
                                                </p>
                                            </div>

                                            <p
                                                className={`text-xs mt-1 ${isSelf ? "text-[#A9A8C2] text-right" : "text-[#9A99AB] text-left"
                                                    }`}
                                            >
                                                {formatTime(message.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            <div className="p-1 border-t bg-white">
                <div className="flex gap-3 items-center">
                    <div className="flex-1">
                        <input
                            type="text"
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your answer..."
                            className="w-full border border-gray-300 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#5563F6] focus:border-transparent resize-none"
                            disabled={sending}
                            maxLength={500}
                        />
                    </div>
                    <button
                        onClick={handleSendMessage}
                        disabled={!messageText.trim() || sending}
                        className="bg-[#5563F6] text-white p-3 rounded-2xl hover:bg-[#4450d6] disabled:bg-gray-300 disabled:cursor-not-allowed transition duration-200 flex items-center justify-center min-w-[50px]"
                    >
                        {sending ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Send size={20} />
                        )}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {showInfo && (
                    <>
                        <motion.div
                            className="fixed inset-0 bg-black bg-opacity-30 z-40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowInfo(false)}
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.3 }}
                            className="fixed top-0 right-0 w-full sm:w-[380px] h-full bg-white shadow-2xl border-l z-50 flex flex-col"
                        >
                            <div className="flex items-center justify-between p-4 border-b relative w-full">
                                <h2 className="font-semibold text-lg text-gray-800">
                                    Chat Information
                                </h2>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setMenuOpen(prev => !prev)}
                                        className="p-2 hover:bg-gray-100 rounded-full transition"
                                    >
                                        ⋮
                                    </button>

                                    <button
                                        onClick={() => setShowInfo(false)}
                                        className="p-2 hover:bg-gray-100 rounded-full transition"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {menuOpen && (
                                    <div
                                        className="absolute right-0 top-14 w-[260px] bg-white border shadow-xl rounded-xl p-4 z-50"
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <IoIosNotificationsOutline style={{fontSize:"22px"}}/>
                                                <span className="text-[16px] text-[#302B2B]">Notifications</span>
                                            </div>

                                            <div
                                                onClick={toggleNotifications}
                                                className={`relative w-[48px] h-[28px] rounded-full flex items-center cursor-pointer transition
                                                    ${notificationsEnabled ? "bg-[#5563F6]" : "bg-gray-300"}`}
                                            >
                                                <div
                                                    className={`w-[24px] h-[24px] bg-white rounded-full shadow-md transition-transform duration-300
                                                        ${notificationsEnabled ? "translate-x-[20px]" : "translate-x-[2px]"}`}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleLeaveCommunity}
                                            className="flex items-center gap-3 text-[#E1090C] hover:text-red-700 transition-colors w-full py-2"
                                        >
                                           <RxExit style={{fontSize:"22px"}}/>
                                            Leave community
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col items-center text-center mt-6 mb-4">
                                <div className="w-40 h-40 rounded-full overflow-hidden border">
                                    <img
                                        src={community.icon}
                                        alt={community.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <h3 className="font-bold text-lg mt-3">{community.name}</h3>

                                <div className="flex items-center gap-2 text-sm text-[#9A99AB] mt-1">
                                    <span className="w-2.5 h-2.5 bg-[#07AC07] rounded-full"></span>
                                    <span>{(community.joined_count || 0).toLocaleString()} Online</span>
                                </div>
                            </div>

                            <div className="px-4">
                                <div className="flex items-center w-full bg-[#F6F6F6] rounded-xl px-3 py-2 h-[40px]">
                                    <input
                                        type="text"
                                        placeholder="Search"
                                        onChange={(e) => {
                                            const q = e.target.value.toLowerCase();
                                            setMembers((prev) =>
                                                prev.map((m) => ({
                                                    ...m,
                                                    hidden:
                                                        !m.full_name?.toLowerCase().includes(q) &&
                                                        !m.user_email?.toLowerCase().includes(q),
                                                }))
                                            );
                                        }}
                                        className="flex-1 bg-transparent outline-none text-sm text-[#302B2B]"
                                    />
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto mt-4 px-4 space-y-1">
                                {membersLoading ? (
                                    <div className="flex justify-center py-6">
                                        <div className="animate-spin w-6 h-6 border-2 border-[#5563F6] border-t-transparent rounded-full"></div>
                                    </div>
                                ) : (
                                    members
                                        .filter((m) => !m.hidden)
                                        .map((m, idx) => (
                                            <div
                                                key={m.user_email || idx}
                                                className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 rounded-lg px-2 transition"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={m.user_icon || "/default-avatar.png"}
                                                        className="w-10 h-10 rounded-full object-cover"
                                                        alt=""
                                                    />
                                                    <span className="text-[15px] text-[#302B2B] font-medium">
                                                        {m.full_name || m.user_email}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                            {showLeaveModal && (
                                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                                    <div className="bg-white rounded-xl p-6 w-[440px] flex flex-col gap-6">
                                        <div className="flex items-center gap-4">
                                            <img
                                                src={community.icon}
                                                className="w-16 h-16 rounded-full object-cover"
                                            />
                                            <h3 className="text-[20px] font-bold text-[#302B2B]">
                                                {community.name}
                                            </h3>
                                        </div>

                                        <p className="text-[18px] text-[#302B2B]">
                                            Are you sure you want to leave community chat?
                                        </p>

                                        <div className="flex justify-end gap-6">
                                            <button
                                                className="text-[18px] text-[#5563F6]"
                                                onClick={() => setShowLeaveModal(false)}
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                className="text-[18px] text-[#E1090C]"
                                                onClick={confirmLeave}
                                            >
                                                Leave
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}